import React, { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import moment from "moment/moment";

import { changeSalarioAsync } from "../../../../api/Employees";
import { getToken } from "../../../../api/Account";
import { useAviso } from "../../../../components/base";
import { formatearMonto } from "../../../../helpers/formato";
import {
  CampoFecha,
  CampoMonto,
  DialogoFormulario,
  esMontoEscribible,
  fechaCambiada,
  useEnvio,
  useEscritura,
} from "../comunes";

/**
 * Cambiar el salario de un empleado. Muestra el salario actual y, como ayuda bajo el campo, la
 * regla del servidor: no se puede bajar el salario (`EmployeeHelper.ChangeSalarioAsync` responde
 * "Es ilegal bajar el salario" si el actual es mayor que el nuevo; igual se acepta). Es solo un
 * texto de ayuda: no bloquea nada que antes se permitiera. Las únicas validaciones son las de siempre:
 * salario vacío y salario 0.
 *
 * `abierto` es opcional: si falta, se dibuja como diálogo abierto.
 */
const ChangeSalario = ({ id, salarioActual, setShowModal, reload, setReload, abierto }) => {
  const [salario, setSalario] = useState("");
  const [fechaInicio, setFechaInicio] = useState(new Date());
  const [intentado, setIntentado] = useState(false);

  const token = getToken();
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();

  const funtionSetSalario = (value) => {
    if (esMontoEscribible(value)) {
      setSalario(value);
    }
  };

  // Las reglas de siempre, en el mismo orden: vacío y luego cero.
  const errorSalario = () => {
    if (salario.length === 0) return "Ingrese el nuevo salario";
    if (parseFloat(salario) === 0) return "El salario debe ser superior a cero";
    return "";
  };

  const saveChanges = () =>
    enviar(async () => {
      setIntentado(true);
      if (errorSalario()) return;

      const datos = {
        employeeId: id,
        nuevoSalario: salario,
        fechaDesde: moment(fechaInicio).format("YYYY-MM-DD"),
      };

      const ok = await escribir(
        () => changeSalarioAsync(token, datos),
        "No se pudo cambiar el salario"
      );
      if (!ok) return;

      setReload(!reload);
      setShowModal(false);
      aviso.exito("Salario actualizado");
    });

  if (abierto === false) return null;

  const tieneActual = salarioActual !== undefined && salarioActual !== null;

  return (
    <DialogoFormulario
      titulo="Cambiar salario"
      onCerrar={() => setShowModal(false)}
      hayCambios={salario !== "" || fechaCambiada(fechaInicio)}
      guardando={guardando}
      onEnviar={saveChanges}
      textoEnviar="Guardar nuevo salario"
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        {tieneActual ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: (theme) => theme.palette.soft.primary,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Salario actual
            </Typography>
            <Typography variant="monto" component="p" sx={{ fontSize: "1.25rem" }}>
              {formatearMonto(salarioActual)}
            </Typography>
          </Box>
        ) : null}

        <CampoFecha
          label="Fecha desde"
          value={fechaInicio}
          onChange={(newValue) => setFechaInicio(newValue)}
          disabled={guardando}
          ayuda="No puede ser anterior a la apertura de la nómina actual."
        />

        <CampoMonto
          autoFocus
          required
          label="Nuevo salario"
          value={salario}
          onChange={funtionSetSalario}
          error={intentado ? errorSalario() : ""}
          ayuda={
            tieneActual
              ? `No puede ser menor que el salario actual (${formatearMonto(salarioActual)}).`
              : "No puede ser menor que el salario actual."
          }
          disabled={guardando}
        />
      </Stack>
    </DialogoFormulario>
  );
};

export default ChangeSalario;
